import { ComponentType, FC, HTMLProps, MouseEvent, ReactNode, Ref, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useFloatingTree } from '@floating-ui/react';

import { AnalyticsProps, buildAnalyticsAttributes } from 'support/helpers/analytics/analytics';
import { cn } from 'support/helpers/cn/cn';
import { ButtonType } from 'components/Form/Button/Button';

import { useMenuContext, useMenuListContext } from './contexts';
import { defaultContainerClasses, SIZE_CLASSNAMES } from './styles';

type MenuButtonOptionProps = {
  to?: string;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  children: ReactNode;
  Icon?: ComponentType<any>;
  testId?: string;
  className?: string;
  size?: ButtonType['size'];
  newGroup?: boolean;
  disabled?: boolean;
} & AnalyticsProps;

export const MenuButtonOption: FC<MenuButtonOptionProps> = ({
  onClick,
  size: sizeProp,
  to,
  children,
  Icon,
  testId,
  className,
  newGroup,
  disabled,
  analyticsId,
  analyticsProperties,
}) => {
  const menu = useMenuContext();
  const size = sizeProp ?? menu.size ?? 'base';
  const tree = useFloatingTree();
  const label = typeof children === 'string' ? children : undefined;

  const containerClasses = cn(
    defaultContainerClasses,
    SIZE_CLASSNAMES[size],
    className,
    newGroup ? 'border-t border-gray-100' : undefined,
  );

  const closeAll = useCallback(() => {
    tree?.events.emit('close');
  }, [tree]);

  const content = (
    <>
      {Icon && <Icon className="mr-2 -ml-1 size-5" />}
      {children}
    </>
  );

  if (to) {
    return (
      <Item label={label}>
        {({ setRef, itemProps }) => (
          <Link
            to={to}
            ref={setRef as unknown as Ref<HTMLAnchorElement>}
            className={containerClasses}
            {...buildAnalyticsAttributes({ analyticsId, analyticsProperties })}
            {...itemProps}
            {...(testId ? { 'data-testid': testId } : {})}
            onClick={(event) => {
              itemProps.onClick?.(event);
              closeAll();
            }}
          >
            {content}
          </Link>
        )}
      </Item>
    );
  }

  return (
    <Item label={label}>
      {({ setRef, itemProps }) => (
        <button
          ref={setRef as unknown as Ref<HTMLButtonElement>}
          data-testid={testId}
          className={containerClasses}
          {...buildAnalyticsAttributes({ analyticsId, analyticsProperties })}
          {...itemProps}
          type="button"
          disabled={disabled}
          onClick={(event) => {
            itemProps.onClick?.(event);
            onClick?.(event);
            closeAll();
          }}
        >
          {content}
        </button>
      )}
    </Item>
  );
};

type ItemRenderProps = Omit<HTMLProps<HTMLElement>, 'ref'> & {
  onClick?: (event: MouseEvent<HTMLElement>) => void;
};

type ItemRenderArgs = {
  setRef: (node: HTMLElement | null) => void;
  itemProps: ItemRenderProps;
};

type ItemProps = {
  label: string | undefined;
  children: (args: ItemRenderArgs) => ReactNode;
};

const Item: FC<ItemProps> = ({ label, children }) => {
  const menu = useMenuContext();
  const { listItemsRef, listContentRef } = useMenuListContext();
  const ref = useRef<HTMLElement | null>(null);
  const indexRef = useRef<number>(-1);

  const setRef = useCallback(
    (node: HTMLElement | null) => {
      ref.current = node;
      if (node) {
        const existingIndex = listItemsRef.current.indexOf(node);
        if (existingIndex === -1) {
          listItemsRef.current.push(node);
          indexRef.current = listItemsRef.current.length - 1;
        } else {
          indexRef.current = existingIndex;
        }
        listContentRef.current[indexRef.current] = label ?? null;
      } else {
        listItemsRef.current = listItemsRef.current.filter((item) => item !== null);
      }
    },
    [label, listItemsRef, listContentRef],
  );

  const { ref: _ignoredRef, ...itemProps } = menu.getItemProps({
    tabIndex: menu.activeIndex === indexRef.current ? 0 : -1,
    role: 'menuitem',
  }) as { ref?: unknown } & ItemRenderProps;

  return <>{children({ setRef, itemProps })}</>;
};
